import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { HeaderComponent } from '../../shared/components/header/header.component';
import { ConsumerSidebarComponent } from '../sidebar/consumer-sidebar.component';
import { PageHeaderComponent } from '../../shared/components/page-header/page-header.component';
import { AuthApiService } from '@angular-workspace/services';
import { DataProviderEntityFormComponent, DataProviderEntityFormData } from '../../shared/components/data-provider-entity-form/data-provider-entity-form.component';
import { ConfirmationDialogComponent } from '../../shared/components/confirmation-dialog/confirmation-dialog.component';
import { ToasterService } from '../../services/toaster.service';

@Component({
  selector: 'app-consumer-data-provider-entity',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    HeaderComponent,
    ConsumerSidebarComponent,
    PageHeaderComponent,
    DataProviderEntityFormComponent,
    ConfirmationDialogComponent,
  ],
  templateUrl: './data-provider-entity.component.html',
  styleUrl: './data-provider-entity.component.scss',
})
export class ConsumerDataProviderEntityComponent implements OnInit {
  @ViewChild('sidebar') sidebarRef?: ConsumerSidebarComponent;

  // Form data for shared component
  formData: DataProviderEntityFormData = {};
  
  // Join organisation form (for organisation_join request type)
  joinOrganisationForm!: FormGroup;

  userOrganisation: any = null;
  showWithdrawConfirm = false;
  withdrawing = false;

  constructor(
    private authApiService: AuthApiService,
    private fb: FormBuilder,
    private toasterService: ToasterService,
    private router: Router
  ) {}

  async ngOnInit(): Promise<void> {
    // Initialize join organisation form
    this.initJoinOrganisationForm();
    await this.loadDataProviderEntityData();
  }
  
  initJoinOrganisationForm(): void {
    this.joinOrganisationForm = this.fb.group({
      userName: [{ value: '', disabled: true }],
      registeredEmail: [{ value: '', disabled: true }],
      entityName: [{ value: '', disabled: true }],
      jobTitle: [{ value: '', disabled: true }],
      employeeId: [{ value: '', disabled: true }]
    });
  }

  // Request type from organisation object
  requestType?: string;
  organisationStatus: 'pending' | 'granted' | null = null;

  async loadDataProviderEntityData(): Promise<void> {
    try {
      const userData = await this.authApiService.getUserInfo();
      
      if (userData && userData['result']) {
        const result = userData['result'];
        const organisation = result['organisation'] || result['organization'] || {};
        
        // Extract request_type from organisation object
        this.requestType = organisation['request_type'] || organisation['requestType'] || result['request_type'] || result['requestType'] || undefined;
        
        // Extract organisation status and store for withdraw
        if (organisation && organisation.status) {
          this.organisationStatus = organisation.status === 'pending' || organisation.status === 'granted' ? organisation.status : null;
          this.userOrganisation = organisation;
        }
        
        // If it's a join request, populate the join organisation form
        if (this.requestType === 'organisation_join') {
          await this.populateJoinOrganisationForm(result, organisation);
        }
        
        // Helper function to format entity type for display
        const formatEntityType = (value: string): string => {
          if (!value) return '';
          // Convert to PascalCase for display
          return value
            .split(/[_\s-]/)
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
        };

        // Helper function to format entity sector for display
        const formatEntitySector = (value: string): string => {
          if (!value) return '';
          // Convert to PascalCase for display
          return value
            .split(/[_\s-]/)
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
        };

        // Extract document file names and paths if available (handle both snake_case and camelCase)
        const documentFiles: { [key: string]: string } = {};
        const documentPaths: { [key: string]: string } = {};
        
        const certPath = organisation['certificatePath'] || organisation['certificate_path'] || '';
        if (certPath) {
          documentFiles['incorporation'] = certPath.split('/').pop() || certPath;
          documentPaths['incorporation'] = certPath; // Store full path for PDF viewing
        }
        
        const panPath = organisation['pancardPath'] || organisation['pancard_path'] || '';
        if (panPath) {
          documentFiles['panCard'] = panPath.split('/').pop() || panPath;
          documentPaths['panCard'] = panPath; // Store full path for PDF viewing
        }
        
        const authPath = organisation['orgDocuments'] || organisation['org_documents'] || organisation['letterOfAuthorization'] || organisation['letter_of_authorization'] || '';
        if (authPath) {
          documentFiles['authorization'] = authPath.split('/').pop() || authPath;
          documentPaths['authorization'] = authPath; // Store full path for PDF viewing
        }
        
        const relPath = organisation['relevantDocPath'] || organisation['relevant_doc_path'] || '';
        if (relPath) {
          documentFiles['others'] = relPath.split('/').pop() || relPath;
          documentPaths['others'] = relPath; // Store full path for PDF viewing
        }

        // Prepare form data for shared component
        this.formData = {
          // User Details
          firstName: result['familyName'] || result['givenName'] || '',
          lastName: result['givenName'] || result['familyName'] || '',
          registeredEmail: result['email'] || '',
          jobTitle: result['jobTitle'] || organisation['jobTitle'] || result['job_title'] || organisation['job_title'] || '',
          employeeId: result['empId'] || result['employeeId'] || result['employee_id'] || organisation['empId'] || organisation['employeeId'] || organisation['employee_id'] || '',
          
          // Entity Details from organisation object
          entityType: formatEntityType(organisation['entityType'] || organisation['entity_type'] || organisation['type'] || ''),
          entitySector: formatEntitySector(organisation['orgSector'] || organisation['org_sector'] || organisation['sector'] || organisation['entitySector'] || organisation['entity_sector'] || ''),
          entityName: organisation['organisationName'] || organisation['organizationName'] || organisation['organisation_name'] || organisation['organization_name'] || organisation['orgName'] || organisation['org_name'] || organisation['name'] || '',
          websiteUrl: organisation['websiteUrl'] || organisation['website_url'] || organisation['website'] || organisation['url'] || organisation['websiteLink'] || organisation['website_link'] || '',
          address: organisation['address'] || organisation['fullAddress'] || organisation['full_address'] || '',
          
          // Document files
          documentFiles: documentFiles,
          // Document paths for PDF viewing
          documentPaths: documentPaths,
        };
      }
    } catch (error) {
      console.error('Error loading organisation data:', error);
    }
  }
  
  async populateJoinOrganisationForm(result: any, organisation: any): Promise<void> {
    // Get user name (combine firstName and lastName or use userName)
    const firstName = result['givenName'] || result['givenName'] || '';
    const lastName = result['familyName'] || result['familyName'] || '';
    const fullName = result['userName'] || result['user_name'] || `${firstName} ${lastName}`.trim() || '';
    
    // Get email
    const email = result['email'] || organisation['officialEmail'] || organisation['official_email'] || '';
    
    // Get organisation ID from organisation object or result level
    const userOrgId = organisation['organisationId'] || 
                     organisation['organizationId'] || 
                     organisation['organisation_id'] || 
                     organisation['organization_id'] ||
                     organisation['id'] ||
                     result['organisationId'] ||
                     result['organizationId'] ||
                     result['organisation_id'] ||
                     result['organization_id'] ||
                     null;
    
    // Get organisation name by calling getAllOrganisations API and mapping the ID
    let orgName = '';
    if (userOrgId) {
      try {
        const response = await this.authApiService.getAllOrganisations(1, 100);
        
        // Extract organisations from response
        let organisations: any[] = [];
        if (response && response['result'] && Array.isArray(response['result'])) {
          organisations = response['result'];
        } else if (Array.isArray(response)) {
          organisations = response;
        }
        
        // Find the organisation by matching organisationId with id
        const matchedOrg = organisations.find(org => 
          org.id === userOrgId || 
          org['id'] === userOrgId ||
          org.organisationId === userOrgId ||
          org['organisationId'] === userOrgId ||
          org.organizationId === userOrgId ||
          org['organizationId'] === userOrgId
        );
        
        if (matchedOrg) {
          orgName = matchedOrg.orgName || matchedOrg['orgName'] || matchedOrg.name || matchedOrg['name'] || matchedOrg.organisationName || matchedOrg['organisationName'] || matchedOrg.organizationName || matchedOrg['organizationName'] || '';
        }
      } catch (error) {
        console.error('Error loading organisations to get organisation name:', error);
      }
    }
    
    // Fallback to organisation name from organisation object if not found in list
    if (!orgName) {
      orgName = organisation['name'] || organisation['organisationName'] || organisation['organizationName'] || organisation['organisation_name'] || organisation['organization_name'] || organisation['orgName'] || organisation['org_name'] || '';
    }
    
    // Get job title
    const jobTitle = organisation['jobTitle'] || organisation['job_title'] || result['jobTitle'] || result['job_title'] || '';
    
    // Get employee ID
    const employeeId = organisation['empId'] || organisation['emp_id'] || organisation['employeeId'] || organisation['employee_id'] || result['empId'] || result['employeeId'] || '';
    
    // Populate form (fields are already disabled)
    this.joinOrganisationForm.patchValue({
      userName: fullName,
      registeredEmail: email,
      entityName: orgName,
      jobTitle: jobTitle,
      employeeId: employeeId
    });
  }
  
  get isJoinRequest(): boolean {
    return this.requestType === 'organisation_join';
  }

  get isCreateRequest(): boolean {
    return this.requestType === 'organisation_create';
  }

  get shouldShowCreateForm(): boolean {
    return this.organisationStatus === 'pending' && this.isCreateRequest;
  }

  onWithdrawClick(): void {
    this.showWithdrawConfirm = true;
  }

  onWithdrawConfirmClose(): void {
    this.showWithdrawConfirm = false;
  }

  async onWithdrawConfirm(): Promise<void> {
    if (!this.userOrganisation) {
      this.toasterService.error('Unable to withdraw: organisation data not found.');
      this.showWithdrawConfirm = false;
      return;
    }
    const requestId = this.userOrganisation.requestId ?? this.userOrganisation.request_id ?? this.userOrganisation.joinRequestId ?? this.userOrganisation.join_request_id ?? this.userOrganisation.id;
    if (!requestId) {
      this.toasterService.error('Unable to withdraw: request details not found. Please try again later.');
      this.showWithdrawConfirm = false;
      return;
    }
    this.withdrawing = true;
    try {
      await this.authApiService.withdrawOrganisationRequest(requestId);
      this.toasterService.success('Your request has been withdrawn successfully.');
      this.showWithdrawConfirm = false;
      this.userOrganisation = null;
      this.organisationStatus = null;
      this.requestType = undefined;
      // Update sidebar immediately so "Awaiting Approval" becomes "Join an Organisation"
      await this.sidebarRef?.refreshOrganisationStatus(true);
      // Navigate to Join Organisation page (Join org form)
      this.router.navigateByUrl('/consumer/join-organisation?withdrawn=1');
    } catch (error) {
      console.error('Error withdrawing request:', error);
      this.toasterService.error('Unable to withdraw request. Please try again later.');
    } finally {
      this.withdrawing = false;
    }
  }
}
